import React, { useState, useEffect, useRef, Fragment, useCallback } from 'react';
import { withRouter } from 'react-router-dom';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import H3Text from '@Uilib/h3Text';
import H3Button, { defaultButtonTypes } from '@Uilib/h3Button';
import UserActions from '@Actions/userActions';

import './style.scss';

const VerifyOtp = ({
  otpRequestResponse,
  verifyOtp,
  verifyOtpLoading,
  verifyOtpError,
  resendOtpLoading,
  resendOtp,
  secondsRemaining,
  startTimer,
  requestOtpSuccess,
}) => {
  const [otpInput, setOtpInput] = useState('');
  const inputRef = useRef(null);

  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.focus();
    }
  }, []);

  const handleLogin = useCallback(() => {
    if (!otpRequestResponse?.user?.sessionId) return;

    verifyOtp({
      type: otpRequestResponse.requestType,
      value: otpRequestResponse.requestValue,
      otp: otpInput,
      sessionId: otpRequestResponse.user.sessionId,
    });
  }, [otpRequestResponse, otpInput, verifyOtp]);

  const handleResend = useCallback(() => {
    if (secondsRemaining <= 0 && otpRequestResponse?.user?.sessionId) {
      resendOtp(otpRequestResponse.user.sessionId);
      startTimer();
    }
  }, [secondsRemaining, otpRequestResponse, resendOtp, startTimer]);

  const handleChangeUser = useCallback(() => {
    requestOtpSuccess(null, null, null);
  }, [requestOtpSuccess]);

  return (
    <Fragment>
      {otpRequestResponse && (
        <H3Text
          text="Enter the verification code sent on your email and phone number"
          className="login__helper-text no-border"
        />
      )}

      <div className="otp-input-wrapper">
        <div className="input-background">
          {[0, 1, 2, 3].map((i) => (
            <div className="input-placeholder-wrapper" key={i}>
              {otpInput.length <= i && <div className="input-placeholder" />}
            </div>
          ))}
        </div>
        <input
          type="text"
          className="otp-input"
          pattern="\d*"
          maxLength={4}
          value={otpInput}
          onChange={(e) => setOtpInput(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && handleLogin()}
          ref={inputRef}
        />
      </div>

      {verifyOtpError && (
        <div className="verify-otp-error-wrapper">
          <div className="verify-otp-error">{verifyOtpError}</div>
        </div>
      )}

      <div className="login-button-wrapper">
        <H3Button
          text="Login"
          buttonType={defaultButtonTypes.BLUE_ROUNDED}
          onClick={handleLogin}
          icon
          style={{ marginTop: '20px' }}
          isLoading={verifyOtpLoading || resendOtpLoading}
        />

        {otpRequestResponse?.user && (
          <div className="login-change-input">
            Not {otpRequestResponse.user.first_name}?&nbsp;
            <span className="login-change-input-href" onClick={handleChangeUser}>
              Click here
            </span>
            &nbsp;to login with a different {otpRequestResponse.requestType === 'email' ? 'email' : 'contact number'}
          </div>
        )}

        <div
          onClick={handleResend}
          className={`login__resend-text ${secondsRemaining > 0 ? 'login__resend-text-disabled' : 'login__resend-text-active'}`}
        >
          {secondsRemaining > 0 ? `Resend OTP in ${secondsRemaining} seconds` : 'Resend OTP'}
        </div>
      </div>
    </Fragment>
  );
};

VerifyOtp.propTypes = {
  otpRequestResponse: PropTypes.any,
  verifyOtp: PropTypes.func,
  verifyOtpLoading: PropTypes.bool,
  verifyOtpError: PropTypes.any,
  resendOtpLoading: PropTypes.bool,
  resendOtp: PropTypes.func,
  secondsRemaining: PropTypes.number,
  startTimer: PropTypes.func,
  requestOtpSuccess: PropTypes.func,
};

const mapStateToProps = ({ UserReducers }) => ({
  verifyOtpLoading: UserReducers.verifyOtpLoading,
  otpRequestResponse: UserReducers.otpRequestResponse,
  verifyOtpError: UserReducers.verifyOtpError,
  resendOtpLoading: UserReducers.resendOtpLoading,
});

const mapDispatchToProps = (dispatch) => ({
  verifyOtp: (payload) => dispatch(UserActions.verifyOtp(payload)),
  resendOtp: (sessionId) => dispatch(UserActions.resendOtp(sessionId)),
  requestOtpSuccess: (otpResponse, requestType, requestValue) =>
    dispatch(UserActions.requestOtpSuccess(otpResponse, requestType, requestValue)),
});

export default connect(mapStateToProps, mapDispatchToProps)(withRouter(VerifyOtp));
